/**
 * Session restore. The Nest backend already implements `GET /api/auth/session`, which verifies the
 * signed `portal_session` cookie and returns the principal + CSRF token — but the SPA never called
 * it, so a full page load always dropped the operator back to the passkey gate even with a valid,
 * unexpired session. That made every refresh and every bookmarked deep link (`/s3/court/docket`)
 * unusable. This module is the missing client half.
 *
 * Contract (verified live): valid cookie → 200 with `{principalId, csrfToken}`; missing, malformed,
 * or expired cookie → 200 with an EMPTY body (Nest's serialization of a `null` return), which
 * `apiGet` normalizes to `null` via the nullable schema below.
 */
import { z } from 'zod';
import { apiGet, setCsrfToken } from './api';

export const sessionSchema = z.object({ principalId: z.string(), csrfToken: z.string() });
export type Session = z.infer<typeof sessionSchema>;

/**
 * Resolve the existing session, or `null` if there isn't a usable one. Never throws: a restore
 * failure (offline, backend restarting, refused) is not an error condition — it just means the
 * operator has to run the passkey ceremony, which is the pre-existing behaviour.
 */
export async function restoreSession(): Promise<Session | null> {
  try {
    const session = await apiGet('/auth/session', sessionSchema.nullable());
    if (!session) return null;
    // The CSRF token must be installed here too, or a restored session could read but every
    // mutation would be refused by the double-submit guard.
    setCsrfToken(session.csrfToken);
    return session;
  } catch {
    return null;
  }
}
